import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'
import { useToast } from '@/components/ui/use-toast'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// Output types
export type TaxExemptionsListOutput = OutputRouter['taxExemptions']['list']
export type TaxExemptionItem = TaxExemptionsListOutput['items'][number]
export type BulkCreateTaxExemptionsOutput = OutputRouter['taxExemptions']['bulkCreate']
export type DeleteTaxExemptionsOutput = OutputRouter['taxExemptions']['delete']

// Input types
export type ListTaxExemptionsInput = InputRouter['taxExemptions']['list']
export type BulkCreateTaxExemptionsInput = InputRouter['taxExemptions']['bulkCreate']
export type DeleteTaxExemptionsInput = InputRouter['taxExemptions']['delete']

// Query hooks
export const useTaxExemptionsList = (params: ListTaxExemptionsInput, options?: {
	enabled?: boolean;
	refetchOnMount?: boolean | 'always';
	refetchOnWindowFocus?: boolean;
}) => {
	const { enabled = true, ...otherOptions } = options || {};
	return trpc.taxExemptions.list.useQuery(params, {
		enabled,
		...otherOptions,
	})
}

// Mutation hooks
export const useBulkCreateTaxExemptions = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.taxExemptions.bulkCreate.useMutation({
		onSuccess: () => {
			utils.taxExemptions.list.invalidate()
			toast({
				title: 'Success',
				description: 'Tax exemptions created successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to create tax exemptions',
				variant: 'destructive',
			})
		},
	})
}

export const useDeleteTaxExemptions = () => {
	const { toast } = useToast()
	const utils = trpc.useUtils()

	return trpc.taxExemptions.delete.useMutation({
		onSuccess: () => {
			utils.taxExemptions.list.invalidate()
			toast({
				title: 'Success',
				description: 'Tax exemption(s) deleted successfully',
			})
		},
		onError: (error) => {
			toast({
				title: 'Error',
				description: error.message || 'Failed to delete tax exemption(s)',
				variant: 'destructive',
			})
		},
	})
}
